import axios from "axios";
import jsonrepair from "jsonrepair";
import FuseUtils from "../utils/FuseUtils";
import localize from "./localize";
import {
  GetDateInYYYYMMDDWithoutTimeFormat,
  getFieldTypeName,
  getOperationalStatusNameFromId,
  GetDateInYYYYMMDDHHmmssUTC,
  GetDateInMMDDYYYYFormat,
  getFirstDayOfMonth,
  EvidenceTypeEnum,
  OperationalStatusEnum,
  GeneralStatusEnum,
  GeneralRequestStatusEnum,
} from "./utils";

export const FormResultsFormatEnum = Object.freeze({
  TwoColumns: 0,
  SingleColumn: 1,
});

export const PaymentOptionsEnum = Object.freeze({
  FixedValue: 0,
  Percentage: 1,
});

export const NotificationTypeEnum = Object.freeze({
  WebAndApp: 1,
  OnlyWeb: 2,
  OnlyApp: 3,
});

export const AdditionalStatusCodeEnum = Object.freeze({
  InProgress: 0,
  Approved: 1,
  Rejected: 2,
});

export const QuotationRequestStatusEnum = Object.freeze({
  Draft: 0,
  Pending: 1,
  Generated: 2,
  Canceled: 3,
});

export const NotBillingMotivationEnum = Object.freeze({
  Other: 0,
  WarrantyServiceNotBillable: 1,
  ServiceNotExecuted: 2,
  NotBillableService: 3,
});

export const MovementTypeEnum = Object.freeze({
  In: 1,
  Out: 2,
});

export const getPaymentTypeOptions = () => [
  {
    Id: PaymentOptionsEnum.FixedValue,
    Name: localize.fixed_value,
  },
  {
    Id: PaymentOptionsEnum.Percentage,
    Name: `${localize.percentage}`,
  },
];

export const obtainsPaymentTypeOption = (id) => {
  const options = getPaymentTypeOptions();

  const found = options.find((x) => x.Id === id);
  return found;
};

export const getSupplyRequestFilteringOptions = () => [
  {
    Name: "Code",
    FriendlyName: localize.code,
    Value: "",
  },
  {
    Name: "Proceeding",
    FriendlyName: localize.proceeding,
    Value: "",
  },
  {
    Name: "SupplyRequestStatus",
    FriendlyName: localize.request_status,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "EventType",
    FriendlyName: localize.event_type,
    Value: "",
  },
];

export const getPaymentToTechnicianRequestFilteringOptions = () => [
  {
    Name: "Code",
    FriendlyName: localize.code,
    Value: "",
  },
  {
    Name: "Proceeding",
    FriendlyName: localize.proceeding,
    Value: "",
  },
  {
    Name: "SupplyRequestStatus",
    FriendlyName: localize.request_status,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "EventType",
    FriendlyName: localize.event_type,
    Value: "",
  },
];

export const SettlementStatusEnum = Object.freeze({
  Pending: 0,
  Approved: 1,
  Rejected: 2,
  Payed: 3,
  Partial: 4,
});

export const getSettlementStatusName = (status) => {
  switch (status) {
    case SettlementStatusEnum.Pending:
      return localize.settlement_status_pending;
    case SettlementStatusEnum.Approved:
      return localize.settlement_status_approved;
    case SettlementStatusEnum.Rejected:
      return localize.settlement_status_rejected;
    case SettlementStatusEnum.Payed:
      return localize.settlement_status_payed;
    case SettlementStatusEnum.Partial:
      return localize.settlement_status_partial;
    default:
      return localize.no_data;
  }
};

export const getSettlementStatusBackColor = (status) => {
  switch (status) {
    case SettlementStatusEnum.Pending:
      return "#E0E0E0";
    case SettlementStatusEnum.Approved:
      return "#2E7D32";
    case SettlementStatusEnum.Rejected:
      return "#E61C45";
    case SettlementStatusEnum.Payed:
      return "#21469C";
    case SettlementStatusEnum.Partial:
      return "#F59E0B";
    default:
      return "white";
  }
};

export const getSettlementStatusForeColor = (status) =>
  status === SettlementStatusEnum.Pending ? "#202020" : "#FFFFFF";

export const getSettlementNotProcessedMessage = (code) => {
  switch (code) {
    case "NO_PERSONAL_ASSIGNED":
      return localize.settlement_error_no_personal_assigned;
    case "ALREADY_SETTLED":
      return localize.settlement_error_already_settled;
    case "NO_PAYMENT_TYPES_ON_EVENT_TYPE":
      return localize.settlement_error_no_payment_types;
    default:
      return code;
  }
};

export const getSettlementFilteringOptions = () => [
  { Name: "Uuid", FriendlyName: localize.code, Value: "" },
  {
    Name: "SettlementStatus",
    FriendlyName: localize.status,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "CreationDate",
    FriendlyName: localize.date_range,
    FilterValue: {},
    Value: "",
  },
  { Name: "CreatedBy", FriendlyName: localize.created_by, Value: "" },
  {
    Name: "PersonalAssignedForSettlement",
    FriendlyName: localize.personal_assigned,
    FilterValue: {},
    Value: "",
  },
];

export const getAdditionalCostRequestFilteringOptions = () => [
  {
    Name: "AdditionalCostType",
    FriendlyName: localize.additional_cost_type,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "EventType",
    FriendlyName: localize.event_type,
    Value: "",
  },
  {
    Name: "SupplyRequestStatus",
    FriendlyName: localize.request_status,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "CreatedBy",
    FriendlyName: localize.created_by,
    Value: "",
  },
];

export const getCurrencyFromSetup = () => "COP";

export const getUserPersonalId = () => {
  const userData = getUserData();
  return userData?.PersonalId;
};

export const getUserData = () => {
  const rawUserData = localStorage.getItem("_auth_state");
  const parsedUserData = JSON.parse(rawUserData || '""');
  return parsedUserData;
};

export const getListFromJson = (jsonData) => {
  if (jsonData && jsonData.length > 0) {
    const data = JSON.parse(jsonData);
    return data;
  }

  return [];
};

export const getUserName = () => getUserData()?.UserName;

export const getClearEvidenceTypeForTemplate = () => ({
  Id: FuseUtils.generateGUID(),
  Name: "",
  Type: EvidenceTypeEnum.Image,
});

export const getClearSectionModel = (eventId) => ({
  ...getClearBasicModel(),
  EventId: eventId,
});

export const getClearFinishConcept = () => ({
  ...getClearBasicModel(),
  Value: 0,
  ReplaceServiceValue: false,
});

export const getClearBasicModel = () => ({
  Name: "",
  Description: "",
  CreatedBy: getUserData()?.UserName,
});

export const getClearGeneralRequestNote = () => ({
  Note: "",
  GeneralRequestId: 0,
  CreatedBy: getUserData()?.UserName,
});

export const getClearGeneralRequestStatusChange = (
  currentStatus,
  requestId,
) => ({
  InitialStatusId: currentStatus,
  FinalStatusId: 0,
  FinalStatus: null,
  Observation: "",
  GeneralRequestId: requestId,
  CreatedBy: getUserData()?.UserName,
});

export const getClearGeneralRequest = () => ({
  RequestTypeId: GeneralRequestStatusEnum.Pending,
  PersonalId: 0,
  ExpectedSolutionDate: GetDateInYYYYMMDDWithoutTimeFormat(new Date()),
  ExpectedSolutionDateValue: new Date(),
  Observation: "",
  CreatedBy: getUserData()?.UserName,
  Client: {},
  ClientId: 0,
});

export const getClearEvidenceTemplateModel = () => ({
  Name: "",
  Description: "",
  Photos: "[]",
  CreatedBy: getUserData()?.UserName,
});

export const getClearBillingModel = (eventId) => ({
  Code: "",
  GenerationDate: new Date(),
  ModificationDate: new Date(),
  CreatedBy: getUserData()?.UserName,
  EventId: eventId,
  Billed: true,
  Observation: "",
  Value: 0,
});

export const getClearOperationalAreaModel = () => ({
  Name: "",
  Description: "",
  BackgroundColor: "#FFFFFF",
  ForegroundColor: "#000000",
  CreatedBy: getUserData()?.UserName,
});

export const getClearWorkflowModel = () => ({
  ...getClearBasicModel(),
  InitialStatusId: 0,
});

export const getClearEventTypeModel = () => ({
  ...getClearBasicModel(),
  BackgroundColor: "#FFFFFF",
  ForegroundColor: "#000000",
  CreatedBy: getUserData()?.UserName,
  OperationalAreaId: 0,
  operationalArea: null,
  WorkFlowId: 0,
  Workflow: null,
  RequestSignature: true,
  executionValue: 0,
  createNotification: false,
  onlyToDefinedUser: false,
  AttentionDays: 0,
  SemaphoreRed: 0,
  SemaphoreYellow: 0,
  SemaphoreGreen: 0,
  reportTitle: null,
});

export const getClearEventPaymentModel = () => ({
  PaymentDate: new Date(),
  Payed: true,
  NoPaymentObservation: "",
  PaymentMethodId: -1,
  PaymentMethod: null,
  EventIds: [],
});

export const getClearEventStatusModel = () => ({
  ...getClearBasicModel(),
  BackgroundColor: "#FFFFFF",
  ForegroundColor: "#000000",
  OperationalStatus: OperationalStatusEnum.ToExecute,
  operationalStatusModel: {
    Id: OperationalStatusEnum.ToExecute,
    Name: getOperationalStatusNameFromId(OperationalStatusEnum.ToExecute),
  },
  IsInitial: false,
  IsFinal: false,
  emails: undefined,
  notifyByEmail: false,
  notificationType: 1,
  notificationTypeSelected: getNotificationTypes()[0],
  createNotification: false,
  onlyToDefinedUser: false,
});

export const getNotificationTypes = () => [
  {
    Id: NotificationTypeEnum.WebAndApp,
    Name: localize.notification_type_web_app,
  },
  {
    Id: NotificationTypeEnum.OnlyWeb,
    Name: localize.notification_type_web,
  },
  {
    Id: NotificationTypeEnum.OnlyApp,
    Name: localize.notification_type_app,
  },
];

export const getCompanyInformation = () => ({
  Name: "",
  Identification: "",
  Address: "",
  PhoneNumber: "",
  Email: "",
  CreatedBy: getUserData()?.UserName,
});

export const getFooterInformation = (companyData) =>
  !companyData?.ComplianceText ||
  companyData?.ComplianceText.replace(" ", "") === ""
    ? localize.data_protection_policy
    : companyData.ComplianceText;

export const getClearStatusChangeModel = () => ({
  InitialStatusId: 0,
  FinalStatusId: 0,
  DynamicFormId: 0,
  WorkFlowId: 0,
  CreatedBy: getUserData()?.UserName,
});

export const getDefaultCalendarOptions = () => ({
  options: {
    transitionMode: "zoom", // or fade
    startWeekOn: "mon", // or sun
    defaultMode: "month", // or week | day | timeline
    minWidth: 540,
    maxWidth: 540,
    minHeight: 540,
    maxHeight: 540,
  },
  alertProps: {
    open: true,
    color: "info", // info | success | warning | error
    severity: "info", // info | success | warning | error
    message: localize.calendar_message,
    showActionButton: true,
    showNotification: true,
    delay: 1500,
  },
  toolbarProps: {
    showSearchBar: true,
    showSwitchModeButtons: true,
    showDatePicker: true,
  },
});

export const getClearEventFinishReviewModel = (eventInfo) => ({
  Id: eventInfo.Id,
  FinishConcept: eventInfo?.FinishConcept,
  FinishConceptId: eventInfo?.FinishConceptId ?? 0,
  OperationalStatusId: eventInfo.OperationalStatus,
  OperationalStatusSelected: {
    Id: eventInfo.OperationalStatus,
    Name: getOperationalStatusNameFromId(eventInfo.OperationalStatus),
  },
  RequestedBy: getUserData()?.UserName,
  CanEdit: eventInfo?.CanEdit ?? 0, 
  PersonalId: eventInfo?.PersonalAssigned?.id ?? 0,
  PersonalAssigned: eventInfo?.PersonalAssigned,
  Observation: eventInfo.FinishObservation ?? "",
});

export const getClearStatusChangeEventModel = () => ({
  InitialStatusId: 0,
  FinalStatusId: 0,
  ChangeDate: GetDateInYYYYMMDDHHmmssUTC(new Date()),
  IsManual: 1,
  CreatedBy: getUserData()?.UserName,
  Observation: "",
  uuid: "",
  DynamicFormId: 0,
});

export const getClearClientModel = () => ({
  IdentificationType: {
    Id: "Cédula de ciudadanía",
    Name: "Cédula de ciudadanía",
  },
  Identification: "",
  Name: "",
  PhoneNumber: "",
  Address: "",
  Email: "",
  ContactName: "",
  CreatedBy: getUserData()?.UserName,
  StateId: 0,
  State: null,
  CityId: 0,
  City: null,
  clientTypeId: 0,
  ClientType: null,
  AddressComplement: "",
});

export const getClearClientBranchModel = () => ({
  Name: "",
  PhoneNumber: "",
  Address: "",
  Email: "",
  ContactName: "",
  CreatedBy: getUserData()?.UserName,
  StateId: 0,
  State: null,
  CityId: 0,
  City: null,
  AddressComplement: "",
});

export const getClearContractorModel = () => ({
  IdentificationType: {
    Id: "Cédula de ciudadanía",
    Name: "Cédula de ciudadanía",
  },
  Identification: "",
  Name: "",
  PhoneNumber: "",
  Address: "",
  Email: "",
  ContactName: "",
  CreatedBy: getUserData()?.UserName,
  StateId: 0,
  State: null,
  CityId: 0,
  City: null,
  companyLogo: null,
  reportSetup: null,
});

export const getClearProviderModel = () => ({
  IdentificationType: {
    Id: "Nit",
    Name: "Nit",
  },
  Identification: "",
  Name: "",
  PhoneNumber: "",
  Address: "",
  Email: "",
  ContactName: "",
  CreatedBy: getUserData()?.UserName,
  StateId: 0,
  State: null,
  CityId: 0,
  City: null,
});

export const getClearPersonalModel = () => ({
  IdentificationType: {
    Id: "Cédula de ciudadanía",
    Name: "Cédula de ciudadanía",
  },
  Identification: "",
  SurName: "",
  LastName: "",
  PhoneNumber: "",
  Address: "",
  EmailAddress: "",
  ContactName: "",
  CreatedBy: getUserData()?.UserName,
  StateId: 0,
  State: null,
  CityId: 0,
  City: null,
  Contractor: null,
  ContractorId: 0,
  Position: null,
  PositionId: 0,
});

export const getClearFieldValueModel = (eventId, fieldId, pageId) => ({
  EventId: eventId,
  FieldId: fieldId,
  PageId: pageId,
  StatusChangeUUID: "",
  FieldValue: null,
  CreatedBy: getUserData()?.UserName,
  FromWeb: true,
  AdditionalData: "",
});

export const getClearPaymenttoTechnicianModel = () => ({
  EventId: 0,
  PersonalId: 0,
  PaymentTypeId: 0,
  PaymentDate: GetDateInYYYYMMDDWithoutTimeFormat(new Date()),
  Value: 0,
  CreatedBy: getUserData()?.UserName,
  Status: GeneralStatusEnum.Generated,
  PaymentTypeData: undefined,
  PersonalData: undefined,
  Observation: "",
});

export const getClearEventModel = () => {
  const date = new Date();
  return {
    Code: "",
    Address: "",
    ReporterName: "",
    ReporterPhoneNumber: "",
    ReporterEmail: "",
    GpsLattitude: 0,
    GpsLongitude: 0,
    OperationalStatus: OperationalStatusEnum.ToExecute,
    StateId: 0,
    State: null,
    CityId: 0,
    City: null,
    EventTypeId: 0,
    ClientId: 0,
    StatusId: 0,
    TeamId: 0,
    RegisterStatus: 1,
    ParentEventId: "",
    ParentEventCode: "",
    CreatedBy: getUserData()?.UserName,
    Observation: "",
    ExpectedSolutionDateValue: date,
    ExpectedSolutionDate: GetDateInYYYYMMDDWithoutTimeFormat(date),
    ExpectedSolutionFinalDateValue: date,
    ExpectedSolutionFinalDate: GetDateInYYYYMMDDWithoutTimeFormat(date),
    Resolutions: [],
    VehicleId: 0,
    CanEdit: 1,
    executionValue: 0,
    executionFinalValue: 0,
    sparePartsValue: 0,
    sparePartsFinalValue: 0,
    sparePartsRealValue: 0,
    AddressComplement: "",
    ContractorId: 0,
  };
};

export const getClearEvidenceModel = (eventIdentification) => ({
  Id: 0,
  Name: "",
  EvidenceTypeId: EvidenceTypeEnum.Image,
  EvidenceType: EvidenceTypeEnum.Image,
  Path: "",
  eventId: eventIdentification,
  CreatedBy: getUserData()?.UserName,
  GpsLongidute: 0,
  GpsLatitude: 0,
  Available: 1,
  FromWeb: true,
  AdditionalData: "",
});

export const getClearEventFilteringModel = () => ({
  EventId: "",
  ClientId: "",
  StartDate: "",
  EndDate: "",
  EventTypeId: "",
  TeamId: "",
});

export const getClearVehicleModel = () => ({
  Identification: "",
  Soat: new Date(Date.now()).toISOString().split("T")[0],
  TechnicalReview: new Date(Date.now()).toISOString().split("T")[0],
  VehicleTypeId: 0,
  PersonalId: 0,
  Personal: null,
  CreatedBy: getUserData()?.UserName,
});

export const getclearFormFieldModel = () => ({
  Title: "",
  Description: "",
  ValueTypeData: { Id: "STRING", Name: getFieldTypeName("STRING") },
  ValueType: "STRING",
  Mandatory: false,
  VisibleOnMobile: true,
  Options: "",
  CreatedBy: getUserData()?.UserName,
  AvailableOnReview: false,
});

export const getClearEventReviewModel = (eventId) => ({
  Title: "",
  Observation: "",
  EvidenceId: 0,
  EventId: eventId,
  CreatedBy: getUserData()?.UserName,
});

export const getClearTeamModel = () => ({
  Name: "",
  CreatedBy: getUserData()?.UserName,
});

export const getClearResolutionModel = () => ({
  ...getClearBasicModel(),
  Code: "",
});

export const getClearMeasurementUnitModel = () => ({
  Name: "",
  Symbol: "",
  CreatedBy: getUserData()?.UserName,
});

export const getClearSupplyModel = () => ({
  ...getClearBasicModel(),
  Unit: undefined,
  UnitId: 0,
  Quantity: 0,
  Code: "",
  Tags: [],
});

export const getClearDailyRoute = (date) => ({
  ...getClearBasicModel(),
  Name: `${localize.daily_route_title}: ${GetDateInMMDDYYYYFormat(
    date ?? new Date(),
  )}`,
  RouteDateValue: date ?? new Date(),
  RouteDate: GetDateInYYYYMMDDWithoutTimeFormat(
    date ?? new Date(),
    false,
    false,
  ),
  IsPrivate: false,
  PersonalId: 0,
  RouteEvents: [],
  Description: "",
  ManagementId: getUserData()?.ManagementId,
});

export const getClearDailyRouteEvent = (eventInfo) => ({
  Observation: eventInfo.Observation,
  CreatedBy: getUserData()?.UserName,
  ModificationDate: new Date(),
  EventId: eventInfo.Id,
  EventInfo: eventInfo,
  Id: Math.random(),
  IsNew: true,
  ManagementId: getUserData()?.ManagementId,
});

export const getClearAdditionalNewnessModel = () => ({
  ...getClearBasicModel(),
  NeedObservation: true,
});

export const getClearChangePasswordModel = () => ({
  Password: "",
});

export const getClearNotificationPreferences = () => ({
  notifyOrderCreation: true,
  notifyStatusChange: true,
  notifyInventoryMovements: true,
  notifyAdditionalCosts: true,
  notifyNewness: true,
  notifyVehiclesManagement: true,
  notifyPreorder: true,
  notifyGeneralRequests: true,
  userName: getUserData()?.UserName,
  managementId: getUserData()?.ManagementId,
  CreatedBy: getUserData()?.UserName,
});

export const getClearAdditionalCostTypeModel = () => ({
  ...getClearBasicModel(),
  GeneratesPaymentToTechnician: false,
  PaymentMode: "FIXED", // PERCENTAGE
  PaymentValue: 0,
  Defaultvalue: 0,
  PaymentTypeId: 0,
  createNotification: true,
});

export const getClearInventoryMovementType = () => ({
  ...getClearBasicModel(),
  MovementType: MovementTypeEnum.In,
  referencedByUsage: false,
});

export const getClearRequestType = () => ({
  ...getClearBasicModel(),
  BackgroundColor: "#FFFFFF",
  ForegroundColor: "#000000",
  DaysUntilWarning: 3,
  DaysUntilError: 8,
  NeedObservationToFinish: false,
});

export const getClearReportType = () => ({
  ...getClearBasicModel(),
  IsPublic: false,
  ReportSections: getReportSections(0, true),
});

export const getClearInventoryMovement = () => ({
  MovementDate: new Date(),
  Code: "",
  Observation: "",
  IsDraft: false,
  MovementTypeId: 0,
  EventId: 0,
  WarehouseId: 0,
  MovementDetail: null,
  CreatedBy: getUserData()?.UserName,
  Warehouse: null,
  WarehouseId: 0,
});

export const getAdditionalCostStatusName = (statusId) => {
  switch (statusId) {
    case AdditionalStatusCodeEnum.InProgress:
      return localize.in_progress;
    case AdditionalStatusCodeEnum.Approved:
      return localize.approved;
    case AdditionalStatusCodeEnum.Rejected:
      return localize.rejected;
    default:
      return null;
  }
};

export const getAdditionalCostStatusBackColor = (statusId) => {
  switch (statusId) {
    case AdditionalStatusCodeEnum.InProgress:
      return "lightgray";
    case AdditionalStatusCodeEnum.Approved:
      return "green";
    case AdditionalStatusCodeEnum.Rejected:
      return "red";
    default:
      return "white";
  }
};

export const getAdditionalCostStatusForeColor = (statusId) => {
  switch (statusId) {
    case AdditionalStatusCodeEnum.Approved:
    case AdditionalStatusCodeEnum.Rejected:
      return "white";
    default:
      return "black";
  }
};

export const getClearAdditionalCostModel = (eventId) => ({
  Value: 0,
  EventId: eventId,
  AdditionalCostTypeId: 0,
  Observation: "",
  CreatedBy: getUserData()?.UserName,
  Status: 0,
});

export const getClearPaymentTypeModel = () => ({
  ...getClearBasicModel(),
  NeedObservation: false,
  DefaultValue: 0,
  Type: 0,
  TypeModel: getPaymentTypeOptions()[0],
});

export const getClearQuotationSetupModel = () => ({
  Name: "",
  ImageLogoPath: "",
  Slogan: "",
  FooterText: "",
  FinalPhrase: "",
  CreateEventOnAccepted: false,
  CreatedBy: getUserData()?.UserName,
});

export const getClearQuotationRequestModel = () => ({
  Status: QuotationRequestStatusEnum.Draft,
  ClientId: 0,
  EventId: 0,
  Observation: "",
  RequestedBy: "",
  RequestedByPhoneNumber: "",
  CreatedBy: getUserData()?.UserName,
});

export const getNewnessByCode = (code) => {
  const newnessCodeObj = {
    "LOW-BATTERY": localize.low_battery,
    "NORMAL-BATTERY": localize.normal_battery,
    "GPS-ON": localize.gps_on,
    "GPS-OFF": localize.gps_off,
    "DATA-ON": localize.data_on,
    "DATA-OFF": localize.data_off,
    SHUTDOWN: localize.shutdown,
    "AIRPLANE-ON": localize.airplane_on,
    "AIRPLANE-OFF": localize.airplane_off,
  };
  return newnessCodeObj[code];
};

export const getClearClientResolutionModel = () => ({
  ClientId: 0,
  ResolutionId: 0,
  CreatedBy: getUserData()?.UserName,
  AssignmentDate: GetDateInYYYYMMDDWithoutTimeFormat(new Date()),
});

export const getCancelationText = (obs) => {
  if (obs && obs.length > 0) {
    const obsSplitted = obs.split("<< MOTIVO DE CANCELACIÓN >>");
    return obsSplitted[obsSplitted.length - 1];
  }

  return undefined;
};

export const getEvidenceType = (evidenceTypeId) => {
  const evidenceTypeObj = {
    1: localize.photography,
    2: localize.audio,
    3: localize.video,
    4: localize.document,
    5: localize.client_signature,
    6: localize.personal_assigned_signature,
    0: localize.no_data,
  };
  return evidenceTypeObj[evidenceTypeId];
};

export const getClearSupplyUsageModel = () => ({
  SupplyId: 0,
  Quantity: 1,
  CreatedBy: getUserData()?.UserName,
  Status: 0,
});

export const getClearProfileModel = () => ({
  Name: "",
  Description: [],
});

export const getClearDeviceSetup = () => ({
  MinimumBatteryLevel: 30,
  MinimumTimeBetweenGPSUpdatesInMillis: 60000,
  MinimumDistanceChangeInMeters: 3,
  MaximumEvidencesPerEvent: 3,
  MaximumAudioEvidencesPerEvent: 3,
  MaximumVideoEvidencesPerEvent: 3,
  ApplicationBaseUrl: process.env.REACT_APP_API_BASE_URL,
  TimeToRequestEventsInMillis: 30000,
  CreatedBy: getUserData()?.UserName,
  ServiceStartupTimeoutInMillis: 5000,
  MaximumAudioTimeInSeconds: 180,
  SynchronizationIntervalInMillis: 300000,
  SignInBaseUrl: process.env.REACT_APP_API_AUTH_BASE_URL,
  ShowGPSValue: true,
  ShowGPSValueAsDegreesMinutesSeconds: true,
  ShowGPSCity: true,
  AutoAssign: true,
  DaysToShowEvents: 3,
});

export const getClearUserModel = () => ({
  UserName: "",
  UserPassword: "",
  FullName: "",
  UserRoles: [],
  ApplicationId: localize.app_name,
  CompanyId: 0,
});

export const RemoveFiltersFromLocalStorage = () => {
  localStorage.removeItem("events_filter");
  localStorage.removeItem("newness_filter");
  localStorage.removeItem("preorder_filter");
};

export const addFilterToLocalStorage = (filterName, filters) => {
  localStorage.setItem(filterName, JSON.stringify(filters));
};

export const getDateFiltering = (dateValue) => {
  const currentMonth = new Date(dateValue).getMonth() === new Date().getMonth();
  return [
    {
      Name: "CreationDate",
      FriendlyName: localize.date_range,
      FilterValue: {
        Value: GetDateInYYYYMMDDWithoutTimeFormat(
          getFirstDayOfMonth(dateValue),
          false,
          false,
        ),
        AlternateValue: GetDateInYYYYMMDDWithoutTimeFormat(
          currentMonth
            ? new Date()
            : new Date(dateValue.getFullYear(), dateValue.getMonth() + 1, 0),
          false,
          true,
        ),
      },
      Value: `${GetDateInMMDDYYYYFormat(
        dateValue.setDate(1),
      )} - ${GetDateInMMDDYYYYFormat(
        currentMonth
          ? new Date()
          : new Date(dateValue.getFullYear(), dateValue.getMonth() + 1, 0),
      )}`,
    },
  ];
};

export const defaultFilteringForEvent = (defaultValue) => {
  try {
    if (defaultValue) {
      return [
        {
          Name: "CreationDate",
          FriendlyName: localize.date_range,
          FilterValue: {
            Value: GetDateInYYYYMMDDWithoutTimeFormat(
              getFirstDayOfMonth(),
              false,
              false,
            ),
            AlternateValue: GetDateInYYYYMMDDWithoutTimeFormat(
              new Date(),
              false,
              true,
            ),
          },
          Value: `${GetDateInMMDDYYYYFormat(
            new Date().setDate(1),
          )} - ${GetDateInMMDDYYYYFormat(new Date())}`,
        },
      ];
    }

    if (!localStorage.getItem("events_filter")) {
      localStorage.setItem(
        "events_filter",
        JSON.stringify([
          {
            Name: "CreationDate",
            FriendlyName: localize.date_range,
            FilterValue: {
              Value: GetDateInYYYYMMDDWithoutTimeFormat(
                getFirstDayOfMonth(),
                true,
                false,
              ),
              AlternateValue: GetDateInYYYYMMDDWithoutTimeFormat(
                new Date(),
                true,
                true,
              ),
            },
            Value: `${GetDateInMMDDYYYYFormat(
              new Date().setDate(1),
            )} - ${GetDateInMMDDYYYYFormat(new Date())}`,
          },
        ]),
      );
    }

    return JSON.parse(localStorage.getItem("events_filter") ?? []);
  } catch {
    return JSON.parse("[]");
  }
};

export const defaultFilteringForDailyRoutes = (defaultValue) => {
  try {
    if (defaultValue) {
      return [
        {
          Name: "RouteDate",
          FriendlyName: localize.route_date,
          FilterValue: {
            Value: GetDateInYYYYMMDDWithoutTimeFormat(
              getFirstDayOfMonth(),
              false,
              false,
            ),
            AlternateValue: GetDateInYYYYMMDDWithoutTimeFormat(
              new Date(),
              false,
              true,
            ),
          },
          Value: `${GetDateInMMDDYYYYFormat(
            new Date().setDate(1),
          )} - ${GetDateInMMDDYYYYFormat(new Date())}`,
        },
      ];
    }

    if (!localStorage.getItem("daily_routes_filter")) {
      localStorage.setItem(
        "daily_routes_filter",
        JSON.stringify([
          {
            Name: "RouteDate",
            FriendlyName: localize.route_date,
            FilterValue: {
              Value: GetDateInYYYYMMDDWithoutTimeFormat(
                getFirstDayOfMonth(),
                true,
                false,
              ),
              AlternateValue: GetDateInYYYYMMDDWithoutTimeFormat(
                new Date(),
                true,
                true,
              ),
            },
            Value: `${GetDateInMMDDYYYYFormat(
              new Date().setDate(1),
            )} - ${GetDateInMMDDYYYYFormat(new Date())}`,
          },
        ]),
      );
    }

    return JSON.parse(localStorage.getItem("daily_routes_filter") ?? []);
  } catch {
    return JSON.parse("[]");
  }
};

export const defaultFilteringForPreOrders = (defaultValue) => {
  try {
    if (defaultValue) {
      return [
        {
          Name: "DateRange",
          FriendlyName: localize.date_range,
          FilterValue: {
            Value: GetDateInYYYYMMDDWithoutTimeFormat(
              getFirstDayOfMonth(),
              false,
              false,
            ),
            AlternateValue: GetDateInYYYYMMDDWithoutTimeFormat(
              new Date(),
              false,
              true,
            ),
          },
          Value: `${GetDateInMMDDYYYYFormat(
            new Date().setDate(1),
          )} - ${GetDateInMMDDYYYYFormat(new Date())}`,
        },
      ];
    }

    if (!localStorage.getItem("preorder_filter")) {
      localStorage.setItem(
        "preorder_filter",
        JSON.stringify([
          {
            Name: "DateRange",
            FriendlyName: localize.date_range,
            FilterValue: {
              Value: GetDateInYYYYMMDDWithoutTimeFormat(
                getFirstDayOfMonth(),
                true,
                false,
              ),
              AlternateValue: GetDateInYYYYMMDDWithoutTimeFormat(
                new Date(),
                true,
                true,
              ),
            },
            Value: `${GetDateInMMDDYYYYFormat(
              new Date().setDate(1),
            )} - ${GetDateInMMDDYYYYFormat(new Date())}`,
          },
        ]),
      );
    }

    return JSON.parse(localStorage.getItem("preorder_filter") ?? []);
  } catch {
    return JSON.parse("[]");
  }
};

export const defaultFilteringForNewness = (defaultValue) => {
  try {
    if (defaultValue) {
      return [
        {
          Name: "OccurrTime",
          FriendlyName: localize.date_range,
          FilterValue: {
            Value: GetDateInYYYYMMDDWithoutTimeFormat(
              getFirstDayOfMonth(),
              false,
              false,
            ),
            AlternateValue: GetDateInYYYYMMDDWithoutTimeFormat(
              new Date(),
              false,
              true,
            ),
          },
          Value: `${GetDateInMMDDYYYYFormat(
            new Date().setDate(1),
          )} - ${GetDateInMMDDYYYYFormat(new Date())}`,
        },
      ];
    }

    if (!localStorage.getItem("newness_filter")) {
      localStorage.setItem(
        "newness_filter",
        JSON.stringify([
          {
            Name: "OccurrTime",
            FriendlyName: localize.date_range,
            FilterValue: {
              Value: GetDateInYYYYMMDDWithoutTimeFormat(
                getFirstDayOfMonth(),
                true,
                false,
              ),
              AlternateValue: GetDateInYYYYMMDDWithoutTimeFormat(
                new Date(),
                true,
                true,
              ),
            },
            Value: `${GetDateInMMDDYYYYFormat(
              new Date().setDate(1),
            )} - ${GetDateInMMDDYYYYFormat(new Date())}`,
          },
        ]),
      );
    }

    return JSON.parse(localStorage.getItem("newness_filter") ?? []);
  } catch {
    return JSON.parse("[]");
  }
};

export const getAdditionalCostsTotalValue = (additionalCostsList) => {
  if (additionalCostsList?.length > 0) {
    let sum = 0;
    additionalCostsList.map((x) => (sum += x.Status === 1 ? x.Value ?? 0 : 0));
    return sum;
  }

  return 0;
};

export const getEventTotalValue = (eventInfo, isFinal) => {
  if (!eventInfo) {
    return 0;
  }

  if (isFinal) {
    return (
      eventInfo.sparePartsFinalValue +
      eventInfo.executionFinalValue +
      (eventInfo.AdditionalCostValue ?? 0)
    );
  }

  return (
    eventInfo.sparePartsValue +
    eventInfo.executionValue +
    (eventInfo.AdditionalCostValue ?? 0)
  );
};

export const defaultNotBillingMotivation = () => ({
  Id: NotBillingMotivationEnum.Other,
  Name: localize.other,
});

export const getNotBillingMotivations = () => [
  {
    Id: NotBillingMotivationEnum.WarrantyServiceNotBillable,
    Name: localize.warranty_service_not_billable,
  },
  {
    Id: NotBillingMotivationEnum.ServiceNotExecuted,
    Name: localize.service_not_executed,
  },
  {
    Id: NotBillingMotivationEnum.NotBillableService,
    Name: localize.not_billable_service,
  },
  defaultNotBillingMotivation(),
];

export const getPersonalFilteringOptions = () => [
  {
    Name: "IdentificationType",
    FriendlyName: localize.identification_type,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "Identification",
    FriendlyName: localize.identification,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "SurName",
    FriendlyName: localize.surName,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "LastName",
    FriendlyName: localize.lastName,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "State",
    FriendlyName: localize.department,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "City",
    FriendlyName: localize.city,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "CreatedBy",
    FriendlyName: localize.modified_by,
    FilterValue: {},
    Value: "",
  },
];

export const getEvidencesCount = (template) => {
  if (!template?.Photos) {
    return 0;
  }

  return getListFromJson(template?.Photos)?.length;
};

export const getClientFilteringOptions = () => [
  {
    Name: "ClientType",
    FriendlyName: localize.client_type,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "IdentificationType",
    FriendlyName: localize.identification_type,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "Identification",
    FriendlyName: localize.identification,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "Name",
    FriendlyName: localize.name,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "PhoneNumber",
    FriendlyName: localize.phone_number,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "State",
    FriendlyName: localize.department,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "City",
    FriendlyName: localize.city,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "CreatedBy",
    FriendlyName: localize.modified_by,
    FilterValue: {},
    Value: "",
  },
];

export const getResolutionsFilteringOptions = () => [
  {
    Name: "Code",
    FriendlyName: localize.code,
    FilterValue: {},
    Value: "",
  },
  ...getBasicFilteringOptions(),
];

export const getSuppliesFileringOptions = () => [
  {
    Name: "Code",
    FriendlyName: localize.code,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "Tags",
    FriendlyName: localize.tags,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "Provider",
    FriendlyName: localize.provider,
    FilterValue: {},
    Value: "",
  },
  ...getBasicFilteringOptions(),
];

export const getAdditionlNewnessFilteringOptions = () => [
  ...getBasicFilteringOptions(),
  {
    Name: "NeedObservation",
    FriendlyName: localize.need_observation,
    FilterValue: {},
    Value: "",
  },
];

export const getAdditionalCostTypeFilteringOptions = () => [
  ...getBasicFilteringOptions(),
  {
    Name: "GeneratesPaymentToTechnician",
    FriendlyName: localize.generates_technician_payment,
    FilterValue: {},
    Value: "",
  },
];

export const getInventoryMovementTypeFilteringOptions = () => [
  ...getBasicFilteringOptions(),
  {
    Name: "MovementType",
    FriendlyName: localize.movement_type,
    FilterValue: {},
    Value: "",
  },
];

export const getInventoryMovementHeaderFilteringOptions = () => [
  {
    Name: "Code",
    FriendlyName: localize.code,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "MovementDate",
    FriendlyName: localize.movement_date,
    FilterValue: {
      Value: GetDateInYYYYMMDDWithoutTimeFormat(
        getFirstDayOfMonth(),
        false,
        false,
      ),
      AlternateValue: GetDateInYYYYMMDDWithoutTimeFormat(
        new Date(),
        false,
        true,
      ),
    },
    Value: `${GetDateInMMDDYYYYFormat(
      new Date().setDate(1),
    )} - ${GetDateInMMDDYYYYFormat(new Date())}`,
  },
  {
    Name: "CreationDate",
    FriendlyName: localize.creation_date,
    FilterValue: {
      Value: GetDateInYYYYMMDDWithoutTimeFormat(
        getFirstDayOfMonth(),
        false,
        false,
      ),
      AlternateValue: GetDateInYYYYMMDDWithoutTimeFormat(
        new Date(),
        false,
        true,
      ),
    },
    Value: `${GetDateInMMDDYYYYFormat(
      new Date().setDate(1),
    )} - ${GetDateInMMDDYYYYFormat(new Date())}`,
  },
  {
    Name: "MovementType",
    FriendlyName: localize.movement_type,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "InventoryMovementType",
    FriendlyName: localize.inventory_movement_type,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "Warehouse",
    FriendlyName: localize.warehouse,
    FilterValue: {},
    Value: "",
  },
];

export const getDailyRouteFilteringOptions = () => [
  ...getBasicFilteringOptions(),
  {
    Name: "IsPrivate",
    FriendlyName: localize.edition_level,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "PersonalAssigned",
    FriendlyName: localize.assigned_to,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "RouteDate",
    FriendlyName: localize.route_date,
    FilterValue: {
      Value: GetDateInYYYYMMDDWithoutTimeFormat(
        getFirstDayOfMonth(),
        false,
        false,
      ),
      AlternateValue: GetDateInYYYYMMDDWithoutTimeFormat(
        new Date(),
        false,
        true,
      ),
    },
    Value: `${GetDateInMMDDYYYYFormat(
      new Date().setDate(1),
    )} - ${GetDateInMMDDYYYYFormat(new Date())}`,
  },
];

export const getEventStatusFilteringOptions = () => [
  {
    Name: "Name",
    FriendlyName: localize.name,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "OperationalStatus",
    FriendlyName: localize.operational_status,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "IsInitial",
    FriendlyName: localize.is_initial_status,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "IsFinal",
    FriendlyName: localize.is_final_status,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "createNotification",
    FriendlyName: localize.notification,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "CreatedBy",
    FriendlyName: localize.modified_by,
    FilterValue: {},
    Value: "",
  },
];

export const getDynamicFormFieldFilteringOptions = () => [
  {
    Name: "Title",
    FriendlyName: localize.title,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "ValueType",
    FriendlyName: localize.field_type,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "Mandatory",
    FriendlyName: localize.mandatory,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "VisibleOnMobile",
    FriendlyName: localize.visible_on_mobile,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "CreatedBy",
    FriendlyName: localize.modified_by,
    FilterValue: {},
    Value: "",
  },
];

export const getNameFilterOption = () => [
  {
    Name: "Name",
    FriendlyName: localize.name,
    FilterValue: {},
    Value: "",
  },
];

export const getVehicleFilteringOptions = () => [
  {
    Name: "Identification",
    FriendlyName: localize.identification,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "Soat",
    FriendlyName: localize.soat,
    FilterValue: {
      Value: GetDateInYYYYMMDDWithoutTimeFormat(
        getFirstDayOfMonth(),
        false,
        false,
      ),
      AlternateValue: GetDateInYYYYMMDDWithoutTimeFormat(
        new Date(),
        false,
        true,
      ),
    },
    Value: `${GetDateInMMDDYYYYFormat(
      new Date().setDate(1),
    )} - ${GetDateInMMDDYYYYFormat(new Date())}`,
  },
  {
    Name: "TechnicalReview",
    FriendlyName: localize.technomechanic_review,
    FilterValue: {
      Value: GetDateInYYYYMMDDWithoutTimeFormat(
        getFirstDayOfMonth(),
        false,
        false,
      ),
      AlternateValue: GetDateInYYYYMMDDWithoutTimeFormat(
        new Date(),
        false,
        true,
      ),
    },
    Value: `${GetDateInMMDDYYYYFormat(
      new Date().setDate(1),
    )} - ${GetDateInMMDDYYYYFormat(new Date())}`,
  },
  {
    Name: "CreatedBy",
    FriendlyName: localize.modified_by,
    FilterValue: {},
    Value: "",
  },
];

export const getGeneralRequestFilteringOptions = () => [
  {
    Name: "ExpectedSolutionDate",
    FriendlyName: localize.expected_attention_date,
    FilterValue: {
      Value: GetDateInYYYYMMDDWithoutTimeFormat(
        getFirstDayOfMonth(),
        false,
        false,
      ),
      AlternateValue: GetDateInYYYYMMDDWithoutTimeFormat(
        new Date(),
        false,
        true,
      ),
    },
    Value: `${GetDateInMMDDYYYYFormat(
      new Date().setDate(1),
    )} - ${GetDateInMMDDYYYYFormat(new Date())}`,
  },
  {
    Name: "CreationDate",
    FriendlyName: localize.creation_date,
    FilterValue: {
      Value: GetDateInYYYYMMDDWithoutTimeFormat(
        getFirstDayOfMonth(),
        false,
        false,
      ),
      AlternateValue: GetDateInYYYYMMDDWithoutTimeFormat(
        new Date(),
        false,
        true,
      ),
    },
    Value: `${GetDateInMMDDYYYYFormat(
      new Date().setDate(1),
    )} - ${GetDateInMMDDYYYYFormat(new Date())}`,
  },
  {
    Name: "RequestType",
    FriendlyName: localize.type,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "RequestStatus",
    FriendlyName: localize.status,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "PersonalAssigned",
    FriendlyName: localize.assigned_to,
    FilterValue: {},
    Value: "",
    Note: localize.only_for_my_requests_list,
  },
  {
    Name: "Client",
    FriendlyName: localize.client,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "CreatedBy",
    FriendlyName: localize.created_by,
    FilterValue: {},
    Value: "",
    Note: localize.only_for_assigned_to_me_list,
  },
];

export const getBillsFilteringOptions = () => [
  {
    Name: "Code",
    FriendlyName: localize.bill_code,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "GenerationDate",
    FriendlyName: localize.bill_date,
    FilterValue: {
      Value: GetDateInYYYYMMDDWithoutTimeFormat(
        getFirstDayOfMonth(),
        false,
        false,
      ),
      AlternateValue: GetDateInYYYYMMDDWithoutTimeFormat(
        new Date(),
        false,
        true,
      ),
    },
    Value: `${GetDateInMMDDYYYYFormat(
      new Date().setDate(1),
    )} - ${GetDateInMMDDYYYYFormat(new Date())}`,
  },
  {
    Name: "CreationDate",
    FriendlyName: localize.date_range,
    FilterValue: {
      Value: GetDateInYYYYMMDDWithoutTimeFormat(
        getFirstDayOfMonth(),
        false,
        false,
      ),
      AlternateValue: GetDateInYYYYMMDDWithoutTimeFormat(
        new Date(),
        false,
        true,
      ),
    },
    Value: `${GetDateInMMDDYYYYFormat(
      new Date().setDate(1),
    )} - ${GetDateInMMDDYYYYFormat(new Date())}`,
  },
  {
    Name: "CreatedBy",
    FriendlyName: localize.modified_by,
    FilterValue: {},
    Value: "",
  },
];

export const getReportTypesFilteringOptions = () => [
  {
    Name: "IsPublic",
    FriendlyName: localize.edition_level,
    FilterValue: {},
    Value: "",
  },
  ...getBasicFilteringOptions(),
];

export const getBasicFilteringOptions = () => [
  {
    Name: "Name",
    FriendlyName: localize.name,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "CreatedBy",
    FriendlyName: localize.modified_by,
    FilterValue: {},
    Value: "",
  },
];

export const getFinishConceptFilter = () => {
  return [
    ...getBasicFilteringOptions(),
    {
      Name: "ReplaceServiceValue",
      FriendlyName: localize.replace_service_value,
      FilterValue: {},
      Value: "",
    },
  ];
};

export const getEventTypeFilter = () => {
  return [
    {
      Name: "OperationalArea",
      FriendlyName: localize.operational_area,
      FilterValue: {},
      Value: "",
    },
    {
      Name: "Workflow",
      FriendlyName: localize.workflow,
      FilterValue: {},
      Value: "",
    },
    {
      Name: "RequestSignature",
      FriendlyName: localize.request_signature,
      FilterValue: {},
      Value: "",
    },
    {
      Name: "createNotification",
      FriendlyName: localize.notification,
      FilterValue: {},
      Value: "",
    },
    ...getBasicFilteringOptions(),
  ];
};

export const getEventsFilterOptions = () => [
  {
    Name: "Id",
    FriendlyName: localize.id,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "Code",
    FriendlyName: localize.code,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "CreationDate",
    FriendlyName: localize.date_range,
    FilterValue: {
      Value: GetDateInYYYYMMDDWithoutTimeFormat(
        getFirstDayOfMonth(),
        false,
        false,
      ),
      AlternateValue: GetDateInYYYYMMDDWithoutTimeFormat(
        new Date(),
        false,
        true,
      ),
    },
    Value: `${GetDateInMMDDYYYYFormat(
      new Date().setDate(1),
    )} - ${GetDateInMMDDYYYYFormat(new Date())}`,
  },
  {
    Name: "PaymentMethod",
    FriendlyName: localize.payment_method,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "PaymentStatus",
    FriendlyName: localize.payment_status,
    FilterValue: {
      Value: false,
    },
    Value: localize.not_payed,
  },
  {
    Name: "PaymentDate",
    FriendlyName: localize.payment_date,
    FilterValue: {
      Value: GetDateInYYYYMMDDWithoutTimeFormat(
        getFirstDayOfMonth(),
        false,
        false,
      ),
      AlternateValue: GetDateInYYYYMMDDWithoutTimeFormat(
        new Date(),
        false,
        true,
      ),
    },
    Value: `${GetDateInMMDDYYYYFormat(
      new Date().setDate(1),
    )} - ${GetDateInMMDDYYYYFormat(new Date())}`,
  },
  {
    Name: "Billable",
    FriendlyName: localize.billable,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "BillCode",
    FriendlyName: localize.bill_code,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "BillStatus",
    FriendlyName: localize.billing_status,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "BillDate",
    FriendlyName: localize.bill_date,
    FilterValue: {
      Value: GetDateInYYYYMMDDWithoutTimeFormat(
        getFirstDayOfMonth(),
        false,
        false,
      ),
      AlternateValue: GetDateInYYYYMMDDWithoutTimeFormat(
        new Date(),
        false,
        true,
      ),
    },
    Value: `${GetDateInMMDDYYYYFormat(
      new Date().setDate(1),
    )} - ${GetDateInMMDDYYYYFormat(new Date())}`,
  },
  {
    Name: "TotalValue",
    FriendlyName: localize.total_value,
    FilterValue: {
      Value: 0,
      AlternateValue: 0,
    },
  },
  {
    Name: "executionFinalValue",
    FriendlyName: localize.service_value,
    FilterValue: {
      Value: 0,
      AlternateValue: 0,
    },
  },
  {
    Name: "sparePartsFinalValue",
    FriendlyName: localize.spare_parts_value,
    FilterValue: {
      Value: 0,
      AlternateValue: 0,
    },
  },
  {
    Name: "ExpectedSolutionDate",
    FriendlyName: localize.expected_attention_date,
    FilterValue: {
      Value: GetDateInYYYYMMDDWithoutTimeFormat(
        getFirstDayOfMonth(),
        false,
        false,
      ),
      AlternateValue: GetDateInYYYYMMDDWithoutTimeFormat(
        new Date(),
        false,
        true,
      ),
    },
    Value: `${GetDateInMMDDYYYYFormat(
      new Date().setDate(1),
    )} - ${GetDateInMMDDYYYYFormat(new Date())}`,
  },
  {
    Name: "EventType",
    FriendlyName: localize.event_type,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "Proceeding",
    FriendlyName: localize.proceeding,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "PersonalAssigned",
    FriendlyName: localize.assigned_to,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "Contractor",
    FriendlyName: localize.contractor,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "Client",
    FriendlyName: localize.client,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "OperationalStatus",
    FriendlyName: localize.operational_status,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "CurrentStatus",
    FriendlyName: localize.execution_status,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "State",
    FriendlyName: localize.department,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "City",
    FriendlyName: localize.city,
    FilterValue: {},
    Value: "",
  },
];

export const getPreOrderFilterOptions = () => [
  {
    Name: "CreatedBy",
    FriendlyName: localize.reported_by,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "DateRange",
    FriendlyName: localize.date_range,
    FilterValue: {
      Value: GetDateInYYYYMMDDWithoutTimeFormat(
        getFirstDayOfMonth(),
        false,
        false,
      ),
      AlternateValue: GetDateInYYYYMMDDWithoutTimeFormat(
        new Date(),
        false,
        true,
      ),
    },
    Value: `${GetDateInMMDDYYYYFormat(
      new Date().setDate(1),
    )} - ${GetDateInMMDDYYYYFormat(new Date())}`,
  },
  {
    Name: "EventType",
    FriendlyName: localize.event_type,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "Proceeding",
    FriendlyName: localize.proceeding,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "Code",
    FriendlyName: localize.order_code,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "PreOrderCode",
    FriendlyName: localize.code,
    FilterValue: {},
    Value: "",
  },
];

export const getNewnessFilterOptions = () => [
  {
    Name: "OccurrTime",
    FriendlyName: localize.date_range,
    FilterValue: {
      Value: GetDateInYYYYMMDDWithoutTimeFormat(
        getFirstDayOfMonth(),
        false,
        false,
      ),
      AlternateValue: GetDateInYYYYMMDDWithoutTimeFormat(
        new Date(),
        false,
        true,
      ),
    },
    Value: `${GetDateInMMDDYYYYFormat(
      new Date().setDate(1),
    )} - ${GetDateInMMDDYYYYFormat(new Date())}`,
  },
  {
    Name: "Newness",
    FriendlyName: localize.newness,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "CreatedBy",
    FriendlyName: localize.reported_by,
    FilterValue: {},
    Value: "",
  },
  {
    Name: "Event",
    FriendlyName: `${localize.event} #`,
    FilterValue: {},
    Value: "",
  },
];

export const getProfiles = () => [
  {
    Form: "OPERATIVE_AREA",
    Restriction: "ACCESS",
  },
  {
    Form: "EVENT_TYPE",
    Restriction: "ACCESS",
  },
  {
    Form: "FORM_FIELD",
    Restriction: "ACCESS",
  },
  {
    Form: "DYNAMIC_FORM",
    Restriction: "ACCESS",
  },
  {
    Form: "EXECUTION_STATUS",
    Restriction: "ACCESS",
  },
  {
    Form: "WORKFLOW",
    Restriction: "ACCESS",
  },
  {
    Form: "ADDITIONAL_NEWNESS",
    Restriction: "ACCESS",
  },
  {
    Form: "ADDITIONAL_COST_TYPE",
    Restriction: "ACCESS",
  },
  {
    Form: "PAYMENT_TYPE",
    Restriction: "ACCESS",
  },
  {
    Form: "EVENT",
    Restriction: "ACCESS",
  },
  {
    Form: "REPORTED_NEWNESS",
    Restriction: "ACCESS",
  },
  {
    Form: "REGISTER_PAYMENTS",
    Restriction: "ACCESS",
  },
  {
    Form: "RELATED_BILLS",
    Restriction: "ACCESS",
  },
  {
    Form: "CONTRACTOR",
    Restriction: "ACCESS",
  },
  {
    Form: "PERSONAL",
    Restriction: "ACCESS",
  },
  {
    Form: "MEASUREMENT_UNIT",
    Restriction: "ACCESS",
  },
  {
    Form: "SUPPLY",
    Restriction: "ACCESS",
  },
  {
    Form: "WAREHOUSE",
    Restriction: "ACCESS",
  },
  {
    Form: "CLIENT",
    Restriction: "ACCESS",
  },
  {
    Form: "RESOLUTION",
    Restriction: "ACCESS",
  },
  {
    Form: "VEHICLE_TYPE",
    Restriction: "ACCESS",
  },
  {
    Form: "VEHICLE",
    Restriction: "ACCESS",
  },
  {
    Form: "USER",
    Restriction: "ACCESS",
  },
  {
    Form: "PROFILE",
    Restriction: "ACCESS",
  },
  {
    Form: "POSITION",
    Restriction: "ACCESS",
  },
  {
    Form: "PROCEEDING",
    Restriction: "ACCESS",
  },
  {
    Form: "EVIDENCE_TEMPLATE",
    Restriction: "ACCESS",
  },
  {
    Form: "GENERAL_REQUESTS",
    Restriction: "ACCESS",
  },
  {
    Form: "REQUEST_TYPE",
    Restriction: "ACCESS",
  },
  {
    Form: "PRE_ORDER",
    Restriction: "ACCESS",
  },
  {
    Form: "DAILY_ROUTE",
    Restriction: "ACCESS",
  },
  {
    Form: "INVENTORY_MOVEMENT_TYPE",
    Restriction: "ACCESS",
  },
  {
    Form: "INVENTORY_MOVEMENT",
    Restriction: "ACCESS",
  },
  {
    Form: "PROVIDER",
    Restriction: "ACCESS",
  },
  {
    Form: "SUPPLY_REQUEST",
    Restriction: "ACCESS",
  },
  {
    Form: "ADDITIONAL_COST_REQUEST",
    Restriction: "ACCESS",
  },
  {
    Form: "SETTLEMENT",
    Restriction: "ACCESS",
  },
  {
    Form: "REPORT_TYPE",
    Restriction: "ACCESS",
  },
  {
    Form: "FINISH_CONCEPT",
    Restriction: "ACCESS",
  },
  {
    Form: "PAYMENT_TO_TECHNICIAN_REQUEST",
    Restriction: "ACCESS",
  },
  {
    Form: "EVENT_DETAIL",
    Restriction: "ACCESS",
  },
  {
    Form: "COMPANY_DATA",
    Restriction: "ACCESS",
  },
  {
    Form: "DEVICE_CONFIGURATION",
    Restriction: "ACCESS",
  },
];

export const getDefaultActions = () => ["ACCESS", "CREATE", "MODIFY", "DELETE"];

// Per-tab visibility permissions for the EventDetail page. A checked action
// (Actions[code] === true) means the tab is RESTRICTED (hidden) for the
// profile, consistent with the app's restriction model (see Sidebar).
export const getEventDetailTabActionByTabId = () => ({
  0: "EVENT_DETAIL_TAB_GENERAL_INFO",
  1: "EVENT_DETAIL_TAB_ADDITIONAL_DATA",
  2: "EVENT_DETAIL_TAB_CLIENT_INFO",
  3: "EVENT_DETAIL_TAB_RESOLUTIONS",
  4: "EVENT_DETAIL_TAB_STATUS_HISTORICAL",
  5: "EVENT_DETAIL_TAB_SUPPLIES",
  6: "EVENT_DETAIL_TAB_GENERAL_REQUESTS",
  7: "EVENT_DETAIL_TAB_NEWNESSES",
  8: "EVENT_DETAIL_TAB_EVIDENCES",
  9: "EVENT_DETAIL_TAB_ADDITIONAL_COSTS",
  10: "EVENT_DETAIL_TAB_VISIT_REPORT",
  11: "EVENT_DETAIL_TAB_BILLING",
  12: "EVENT_DETAIL_TAB_EVENT_PAYMENT",
  13: "EVENT_DETAIL_TAB_PAYMENT_TO_TECHNICIAN",
  14: "EVENT_DETAIL_TAB_TECHNICAL_CONCEPT",
  15: "EVENT_DETAIL_TAB_SETTLEMENT",
});

export const getEventDetailTabActions = () =>
  Object.values(getEventDetailTabActionByTabId());

// Shared RBAC check. Reads the current user's profile restrictions and returns
// true when the given action on the given module Form is RESTRICTED (denied).
// Semantics are inverted per the app's model: Actions[code] === true = denied.
export const isModuleActionRestricted = (form, action) => {
  if (!form || !action) return false;
  try {
    const description = getUserData()?.UserRoles?.[0]?.Description;
    if (!description) return false;
    const modules = JSON.parse(jsonrepair(description));
    const mod = modules.find((m) => m.Form === form);
    return mod?.Actions?.[action] === true;
  } catch {
    return false;
  }
};

export const isEventDetailTabRestricted = (tabId) => {
  const code = getEventDetailTabActionByTabId()[tabId];
  if (!code) return false;
  return isModuleActionRestricted("EVENT_DETAIL", code);
};

export const getModuleSpecialActions = () => ({
  EVENT: [
    "SERVICE_VALUE_CHANGE",
    "SERVICE_ASSIGNMENT",
    "BILLING",
    "SERVICE_PAYMENT_REGISTRATION",
    "PERSONAL_PAYMENT_REGISTRATION",
    "PERSONAL_PAYMENT_APPROVAL",
    "SUPPLY_APPROVAL",
    "STATUS_CHANGE",
    "STATUS_CHANGE_FORM_MODIFICATION",
    "ADD_EVIDENCES",
    "DELETE_EVIDENCES",
    "DOWNLOAD_EVIDENCES",
    "ADD_NOTES_TO_EVIDENCES",
    "ADD_ADDITIONAL_COSTS",
    "ADDITIONAL_COSTS_APPROVAL",
    "REPORT_MODIFICATION",
    "ADD_NOTES",
  ],
  VEHICLE: ["VEHICLE_ASSIGNMENT"],
  USER: ["PROFILE_ASSIGNMENT", "USER_CHANGE", "PASSWORD_CHANGE"],
});

export const getModuleActions = (formName) => {
  const specialActions = getModuleSpecialActions();
  const accessOnlyModules = [
    "REGISTER_PAYMENTS",
    "COMPANY_DATA",
    "DEVICE_CONFIGURATION",
  ];
  const accessCreateOnlyModules = ["SETTLEMENT"];
  const accessModifyOnlyModules = ["SUPPLY_REQUEST", "ADDITIONAL_COST_REQUEST"];

  if (formName === "EVENT_DETAIL") {
    return getEventDetailTabActions();
  }

  if (accessOnlyModules.includes(formName)) {
    return ["ACCESS"];
  }

  if (accessCreateOnlyModules.includes(formName)) {
    return ["ACCESS", "CREATE", "SETTLEMENT_STATUS_CHANGE"];
  }

  if (accessModifyOnlyModules.includes(formName)) {
    return ["ACCESS", "MODIFY"];
  }

  const actions = [...getDefaultActions()];
  if (specialActions[formName]) {
    actions.push(...specialActions[formName]);
  }
  return actions;
};

export const getReportSections = (reportTypeValue, allValues) => ({
  eventInfo: allValues ?? true,
  clientInfo: allValues ?? true,
  additionalData: allValues ?? true,
  statusChanges: allValues ?? true,
  supplies: allValues ?? true,
  evidences: allValues ?? true,
  visitReport: allValues ?? true,
  review: allValues ?? true,
  eventResolutions: allValues ?? true,
  generalRequests: allValues ?? true,
  newness: allValues ?? true,
  formSignatures: allValues ?? false,
  additionalCosts: allValues ?? true,
  paymentToTechnician: allValues ?? false,
  settlement: allValues ?? false,
  panel_report: allValues ?? true,
  report_type: reportTypeValue,
  form_results_format: {
    Id: FormResultsFormatEnum.TwoColumns,
    Name: localize.form_results_two_columns,
  },
  hideModificationDate: allValues ?? false,
  useContractorHeader: allValues ?? false,
});

export const getBasicFormatSections = () => ({
  eventInfo: true,
  clientInfo: true,
  additionalData: false,
  statusChanges: false,
  supplies: false,
  evidences: false,
  visitReport: false,
  review: false,
  eventResolutions: false,
  generalRequests: false,
  newness: false,
  formSignatures: true,
  additionalCosts: false,
  paymentToTechnician: false,
  settlement: false,
  panel_report: true,
  report_type: 0,
  columns: 3,
  form_results_format: {
    Id: FormResultsFormatEnum.TwoColumns,
    Name: localize.form_results_two_columns,
  },
  hideModificationDate: false,
  useContractorHeader: false,
});

export const getReportSectionNames = () => [
  { name: "eventInfo", title: localize.general_information },
  { name: "clientInfo", title: localize.client_information },
  { name: "additionalData", title: localize.additional_data },
  { name: "statusChanges", title: localize.status_historical },
  { name: "supplies", title: localize.supplies },
  { name: "evidences", title: localize.evidences },
  { name: "visitReport", title: localize.visit_report },
  { name: "review", title: localize.technical_concept },
  { name: "eventResolutions", title: localize.resolutions },
  { name: "generalRequests", title: localize.general_requests },
  { name: "newness", title: localize.newnesses },
  { name: "formSignatures", title: localize.form_signatures },
  { name: "additionalCosts", title: localize.additional_costs },
  { name: "paymentToTechnician", title: localize.payment_to_technician },
  { name: "settlement", title: localize.settlement_detail_title },
];

export const getMovementTypeName = (id) => {
  if (id === MovementTypeEnum.In) {
    return localize.in;
  }

  if (id === MovementTypeEnum.Out) {
    return localize.out;
  }

  return localize.no_data;
};

export const getMovementTypeColor = (id) => {
  if (id === MovementTypeEnum.In) {
    return "green";
  }

  if (id === MovementTypeEnum.Out) {
    return "crimson";
  }

  return "gray";
};

export const downloadBlob = async (
  url,
  config,
  dataCallBack,
  loadingCallback,
) => {
  loadingCallback && loadingCallback(true);
  await axios({
    ...config,
    method: "get",
    responseType: "blob", // Force to receive data in a Blob Format
    url: url,
  })
    .then((res) => {
      const blob = new Blob([res.data], {
        type: "application/octet-stream",
      });

      const urlBlob = URL.createObjectURL(blob);
      loadingCallback && loadingCallback(false);
      dataCallBack && dataCallBack(urlBlob);
    })
    .catch((error) => {
      loadingCallback && loadingCallback(false);
      console.error("Error downloading blob: " + url, error);
      dataCallBack && dataCallBack(undefined);
    });
};
